import React, { useState } from 'react';
import { Home, Users, BookOpen, BarChart3, Settings as SettingsIcon, Plus, Download } from '../../components/CustomIcons';
import { Btn, Chip, Mono, ScreenHeader } from '../Prototype';
import { StatCard, SectionTitle, StaffShell, Badge, useUI } from './shared';
import { AD_STATS, AD_ALERTS, AD_FACILITIES, AD_USERS, AD_MODULES } from './data';

const NAV = [
  { key: 'dashboard', label: 'Dashboard', icon: Home },
  { key: 'users', label: 'Users', icon: Users },
  { key: 'modules', label: 'Modules', icon: BookOpen },
  { key: 'analytics', label: 'Analytics', icon: BarChart3 },
  { key: 'settings', label: 'Settings', icon: SettingsIcon },
];

const useADState = () => {
  const [state, setState] = useState(() => {
    try {
      const raw = localStorage.getItem('assistivos_admin_state');
      if (raw) return JSON.parse(raw);
    } catch (_e) { /* ignore */ }
    return { tab: 'dashboard', userFilter: 'all', moduleFilter: 'all', approved: {} };
  });
  const update = (patch) => setState((s) => {
    const next = { ...s, ...(typeof patch === 'function' ? patch(s) : patch) };
    try { localStorage.setItem('assistivos_admin_state', JSON.stringify(next)); } catch (_e) { /* ignore */ }
    return next;
  });
  return [state, update];
};

const AD1_Dashboard = ({ update }) => (
  <>
    <ScreenHeader title="Admin" subtitle="Tech Tradesmith Institute" />
    <main className="overflow-y-auto flex-1 px-6 py-5 space-y-5">
      <section aria-label="Platform stats" className="grid grid-cols-3 gap-2">
        <StatCard label="Learners" value={AD_STATS.learners} />
        <StatCard label="Facilities" value={AD_STATS.facilities} />
        <StatCard label="Credentials" value={AD_STATS.credentials} />
        <StatCard label="Employers" value={AD_STATS.employers} />
        <StatCard label="Modules" value={AD_STATS.modules} />
        <StatCard label="Completion" value={`${AD_STATS.completionRate}%`} accent />
      </section>
      <section aria-label="Alerts">
        <SectionTitle>Alerts</SectionTitle>
        <ul className="list-none p-0 space-y-2">
          {AD_ALERTS.map((a) => (
            <li key={a.key} className="rounded-[12px] p-3 flex items-center justify-between gap-3" style={{ background: 'rgba(249,115,22,0.10)', border: '1px solid #f97316' }}>
              <span className="text-[14px] text-[#0f2744] font-semibold">{a.label}</span>
              <button onClick={() => update({ tab: a.href })} data-testid={`ad-alert-${a.key}`} className="text-[13px] font-semibold text-[#c2410c] hover:underline">Review →</button>
            </li>
          ))}
        </ul>
      </section>
      <section aria-label="Facility breakdown">
        <SectionTitle>Facilities</SectionTitle>
        <ul className="list-none p-0 space-y-2">
          {AD_FACILITIES.map((f) => (
            <li key={f.name} className="rounded-[12px] bg-[#f1f5f9] p-4 flex items-center justify-between">
              <div className="min-w-0">
                <div className="text-[14px] font-semibold text-[#0f2744]">{f.name}</div>
                <div className="text-[12px] text-[#64748b] mt-0.5"><Mono>{f.learners}</Mono> learners · <Mono>{f.modules}</Mono> modules</div>
              </div>
            </li>
          ))}
        </ul>
      </section>
    </main>
  </>
);

const AD2_Users = ({ state, update }) => {
  const { showModal, showToast } = useUI();
  const [q, setQ] = useState('');
  const [name, setName] = useState('');
  const rows = AD_USERS.filter((u) => {
    if (state.userFilter !== 'all' && !u.role.toLowerCase().includes(state.userFilter)) return false;
    if (q && !`${u.name} ${u.role} ${u.facility}`.toLowerCase().includes(q.toLowerCase())) return false;
    return true;
  });
  const addUser = () => showModal({
    title: 'Invite a new user',
    body: 'Enter the user\'s full name. Role and facility can be assigned after they accept.',
    input: { placeholder: 'Full name', value: name, onChange: setName },
    confirmLabel: 'Send Invite',
    onConfirm: () => {
      showToast(`Invite sent${name ? ` to ${name}` : ''}.`, 'success');
      setName('');
    },
  });
  return (
    <>
      <ScreenHeader title="User Management" subtitle={`${AD_USERS.length} accounts`} />
      <main className="overflow-y-auto flex-1 px-6 py-5 space-y-3">
        <div className="flex gap-2">
          <input
            type="text"
            placeholder="Search by name, role, or facility..."
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="ad-user-search"
            className="flex-1 min-h-[48px] rounded-[12px] border border-[#e2e8f0] bg-[#f1f5f9] px-4 text-[14px] focus:outline-none focus:border-[#f97316]"
          />
          <Btn onClick={addUser} data-testid="ad-add-user" className="px-3"><Plus className="w-4 h-4" /> Add</Btn>
        </div>
        <div className="flex gap-2 flex-wrap" role="tablist">
          {['all', 'learner', 'youth learner', 'case manager', 'instructor', 'employer', 'admin'].map((f) => (
            <button
              key={f}
              onClick={() => update({ userFilter: f })}
              role="tab"
              aria-selected={state.userFilter === f}
              data-testid={`ad-userfilter-${f.replace(/\s+/g, '-')}`}
              className="rounded-full px-3 py-1.5 text-[12px] font-semibold capitalize"
              style={state.userFilter === f ? { background: '#f97316', color: '#0f2744' } : { background: '#f1f5f9', color: '#64748b' }}
            >{f}</button>
          ))}
        </div>
        <ul className="list-none p-0 space-y-2">
          {rows.map((u) => (
            <li key={u.id} className="rounded-[12px] bg-[#f1f5f9] p-3">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="min-w-0 flex-1">
                  <div className="text-[14px] font-semibold text-[#0f2744]">{u.name}</div>
                  <div className="text-[12px] text-[#64748b]">{u.role} · {u.facility}</div>
                </div>
                <Badge tone={u.status === 'At-risk' ? 'orange' : 'success'}>{u.status}</Badge>
              </div>
              <div className="text-[11px] text-[#94a3b8] mt-1">Last active: {u.lastActive}</div>
            </li>
          ))}
        </ul>
      </main>
    </>
  );
};

const AD3_Modules = ({ state, update }) => {
  const { showModal, showToast } = useUI();
  const rows = AD_MODULES.filter((m) => state.moduleFilter === 'all' || m.status === state.moduleFilter);
  const approve = (name) => showModal({
    title: `Approve ${name}?`,
    body: 'Approving publishes this module to all active facilities.',
    confirmLabel: 'Approve & Publish',
    onConfirm: () => {
      update((s) => ({ approved: { ...s.approved, [name]: true } }));
      showToast(`${name} approved and published.`, 'success');
    },
  });
  return (
    <>
      <ScreenHeader title="Module Catalog" subtitle={`${AD_MODULES.length} modules`} />
      <main className="overflow-y-auto flex-1 px-6 py-5 space-y-3">
        <div className="flex gap-2 flex-wrap" role="tablist">
          {['all', 'active', 'pending', 'draft'].map((f) => (
            <button
              key={f}
              onClick={() => update({ moduleFilter: f })}
              role="tab"
              aria-selected={state.moduleFilter === f}
              data-testid={`ad-modfilter-${f}`}
              className="rounded-full px-3 py-1.5 text-[12px] font-semibold capitalize"
              style={state.moduleFilter === f ? { background: '#f97316', color: '#0f2744' } : { background: '#f1f5f9', color: '#64748b' }}
            >{f}</button>
          ))}
        </div>
        <ul className="list-none p-0 space-y-2">
          {rows.map((m) => {
            const approved = state.approved[m.name];
            const effStatus = approved ? 'active' : m.status;
            return (
              <li key={m.name} className="rounded-[12px] bg-[#f1f5f9] p-3">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <div className="text-[14px] font-semibold text-[#0f2744]">{m.name}</div>
                    <div className="text-[12px] text-[#64748b]">Credential: {m.credential}</div>
                    {effStatus === 'active' && (
                      <div className="text-[12px] text-[#64748b] mt-0.5"><Mono>{m.enrolled}</Mono> enrolled · <Mono>{m.completion}</Mono>% completion</div>
                    )}
                  </div>
                  <Badge tone={effStatus === 'active' ? 'success' : effStatus === 'pending' ? 'orange' : 'neutral'}>{effStatus}</Badge>
                </div>
                {m.status === 'pending' && !approved && (
                  <div className="mt-2 grid grid-cols-2 gap-2">
                    <Btn variant="ghost" onClick={() => showToast('Revision requested. Curriculum team notified.')} data-testid={`ad-revise-${m.name.replace(/\s+/g, '-')}`}>Request Revision</Btn>
                    <Btn onClick={() => approve(m.name)} data-testid={`ad-approve-${m.name.replace(/\s+/g, '-')}`}>Approve</Btn>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
        <Btn variant="ghost" className="w-full" onClick={() => showToast('Contact the TTI curriculum team to add new modules.')} data-testid="ad-add-module">Request New Module</Btn>
      </main>
    </>
  );
};

const AD4_Analytics = ({ update }) => {
  const { showToast } = useUI();
  const modules = AD_MODULES.filter((m) => m.status === 'active').slice(0, 6);
  const maxComp = Math.max(...modules.map((m) => m.completion));
  const [range, setRange] = useState(1);
  const rangeMult = [0.3, 1.0, 3.8][range];
  return (
    <>
      <ScreenHeader title="Analytics" subtitle="Platform performance" />
      <main className="overflow-y-auto flex-1 px-6 py-5 space-y-5">
        <div className="flex gap-2" role="tablist" aria-label="Time range">
          {['This Month', 'Quarter', 'Year'].map((r, i) => (
            <button
              key={r}
              onClick={() => setRange(i)}
              data-testid={`ad-range-${i}`}
              aria-pressed={range === i}
              role="tab"
              className="rounded-full px-3 py-1.5 text-[12px] font-semibold transition-all"
              style={range === i ? { background: '#f97316', color: '#0f2744' } : { background: '#f1f5f9', color: '#64748b' }}
            >{r}</button>
          ))}
        </div>
        <section aria-label="Top metrics" className="grid grid-cols-2 gap-2">
          <StatCard label="Enrollments" value={Math.round(AD_STATS.learners * rangeMult)} unit={['+6%', '+18%', '+42%'][range]} accent />
          <StatCard label="Completions" value={Math.round(AD_STATS.credentials * rangeMult)} unit={['+8%', '+24%', '+51%'][range]} />
          <StatCard label="Credentials" value={Math.round(AD_STATS.credentials * rangeMult)} />
          <StatCard label="Placements" value={Math.round(6 * rangeMult)} />
        </section>
        <section aria-label="Credential completions chart">
          <SectionTitle>Credential Completions by Module</SectionTitle>
          <div className="rounded-[12px] bg-[#f1f5f9] p-4 space-y-2">
            {modules.map((m) => (
              <div key={m.name}>
                <div className="flex items-center justify-between text-[12px]">
                  <span className="text-[#0f2744] font-semibold truncate">{m.name}</span>
                  <span className="p-mono text-[#0f2744]">{m.completion}%</span>
                </div>
                <div className="mt-1 w-full h-1.5 rounded-full overflow-hidden" style={{ background: '#e2e8f0' }}>
                  <div className="h-full rounded-full" style={{ width: `${(m.completion / maxComp) * 100}%`, background: '#f97316' }} />
                </div>
              </div>
            ))}
          </div>
        </section>
        <section aria-label="Placement funnel">
          <SectionTitle>Employer Placement Funnel</SectionTitle>
          <div className="rounded-[12px] bg-[#f1f5f9] p-4 space-y-2">
            {[
              { l: 'Matched', v: 31 },
              { l: 'Interest', v: 14 },
              { l: 'In Process', v: 9 },
              { l: 'Hired', v: 6 },
            ].map((step, i) => (
              <div key={step.l} className="flex items-center gap-3">
                <div className="w-20 text-[12px] font-semibold text-[#64748b]">{step.l}</div>
                <div className="flex-1 h-6 rounded-[6px] flex items-center px-2 text-[12px] font-bold" style={{ width: `${(step.v / 31) * 100}%`, background: i === 3 ? '#15803d' : '#0f2744', color: '#fafaf9', fontFamily: 'var(--p-font-mono)' }}>{step.v}</div>
              </div>
            ))}
          </div>
        </section>
        <section aria-label="Grant reporting">
          <SectionTitle>Grant Reporting</SectionTitle>
          {[
            { r: 'WIOA Title I Report', d: 'Due August 1, 2025' },
            { r: 'DOL RESTART Report', d: 'Due September 15, 2025' },
          ].map((rep) => (
            <div key={rep.r} className="rounded-[12px] bg-[#f1f5f9] p-3 mb-2 flex items-center justify-between">
              <div className="min-w-0">
                <div className="text-[13px] font-semibold text-[#0f2744]">{rep.r}</div>
                <div className="text-[11px] text-[#64748b]">{rep.d}</div>
              </div>
              <Btn variant="ghost" onClick={() => showToast(`${rep.r} generated. Download ready.`, 'success')} data-testid={`ad-report-${rep.r.split(' ')[0]}`}>Generate</Btn>
            </div>
          ))}
        </section>
        <Btn onClick={() => showToast('Analytics exported as .xlsx.', 'success')} className="w-full" data-testid="ad-export"><Download className="w-4 h-4" /> Export All Analytics</Btn>
      </main>
    </>
  );
};

const AD5_Settings = ({ update, onResetDemo, role }) => {
  const { showModal, showToast } = useUI();
  const prefKeys = ['at-risk-alerts', 'weekly-summary', 'grant-reminders'];
  const prefLabels = ['At-risk learner alerts', 'Weekly summary email', 'Grant deadline reminders'];
  const [prefs, setPrefs] = useState(() => {
    try {
      const raw = localStorage.getItem('assistivos_admin_prefs_v1');
      if (raw) return JSON.parse(raw);
    } catch (_e) { /* ignore */ }
    return { 'at-risk-alerts': true, 'weekly-summary': true, 'grant-reminders': true };
  });
  const togglePref = (k) => {
    const next = { ...prefs, [k]: !prefs[k] };
    setPrefs(next);
    try { localStorage.setItem('assistivos_admin_prefs_v1', JSON.stringify(next)); } catch (_e) { /* ignore */ }
    showToast(`${prefLabels[prefKeys.indexOf(k)]} ${next[k] ? 'enabled' : 'disabled'}`, 'success');
  };
  return (
    <>
      <ScreenHeader title="Platform Settings" subtitle="Demo configuration" />
      <main className="overflow-y-auto flex-1 px-6 py-5 space-y-5">
        <section aria-label="Facilities">
          <SectionTitle>Facility Configuration</SectionTitle>
          {AD_FACILITIES.map((f) => (
            <div key={f.name} className="rounded-[12px] bg-[#f1f5f9] p-3 mb-2 flex items-center justify-between">
              <span className="text-[14px] text-[#0f2744] font-semibold">{f.name}</span>
              <span className="rounded-full px-3 py-1 text-[11px] font-bold" style={{ background: '#15803d', color: '#fafaf9' }}>ACTIVE</span>
            </div>
          ))}
        </section>
        <section aria-label="Notifications">
          <SectionTitle>Notification Preferences</SectionTitle>
          {prefKeys.map((k, i) => {
            const on = !!prefs[k];
            return (
              <div key={k} className="rounded-[12px] bg-[#f1f5f9] p-3 mb-2 flex items-center justify-between">
                <span className="text-[14px] text-[#0f2744]">{prefLabels[i]}</span>
                <button
                  type="button"
                  role="switch"
                  aria-checked={on}
                  data-testid={`ad-pref-${k}`}
                  onClick={() => togglePref(k)}
                  className="relative w-11 h-6 rounded-full transition-colors"
                  style={{ background: on ? '#f97316' : '#cbd5e1' }}
                >
                  <span
                    aria-hidden="true"
                    className="absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all"
                    style={{ left: on ? 22 : 2, boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }}
                  />
                </button>
              </div>
            );
          })}
        </section>
        <section aria-label="Demo controls">
          <SectionTitle>Demo Mode</SectionTitle>
          <div className="rounded-[12px] bg-[#f1f5f9] p-3 mb-2">
            <div className="text-[12px] font-bold uppercase tracking-widest text-[#64748b]">Current Role</div>
            <div className="text-[14px] text-[#0f2744] font-semibold mt-1 capitalize">{role.replace('_', ' ')}</div>
          </div>
          <Btn
            variant="ghost"
            onClick={() => showModal({
              title: 'Reset all demo data?',
              body: 'This clears every role\'s progress, saved notes, flagged learners, and expressed interests. The boot sequence will replay.',
              confirmLabel: 'Reset Demo',
              onConfirm: () => {
                onResetDemo && onResetDemo();
                showToast('Demo reset. Reloading...', 'success');
              },
            })}
            className="w-full"
            data-testid="ad-reset-demo"
          >
            Reset All Demo Data
          </Btn>
        </section>
      </main>
    </>
  );
};

const Admin = ({ onResetDemo, role }) => {
  const [state, update] = useADState();
  const tab = state.tab || 'dashboard';
  const onNav = (k) => update({ tab: k });

  let screen;
  if (tab === 'users') screen = <AD2_Users state={state} update={update} />;
  else if (tab === 'modules') screen = <AD3_Modules state={state} update={update} />;
  else if (tab === 'analytics') screen = <AD4_Analytics update={update} />;
  else if (tab === 'settings') screen = <AD5_Settings update={update} onResetDemo={onResetDemo} role={role} />;
  else screen = <AD1_Dashboard update={update} />;

  return (
    <StaffShell items={NAV} activeKey={tab} onChange={onNav} badge={{ initial: 'L', name: 'LaVar McCline', role: 'TTI Admin' }}>
      {screen}
    </StaffShell>
  );
};

export default Admin;
